import { loginSchema, registerSchema } from '@keep/shared';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useAppConfig, useLogin, useMe, useRegister } from '../../api/auth';
import { Button } from '../../components/ui/button';
import { errorMessage } from '../../lib/api';
import { paths } from '../../lib/routes';

const inputClass =
  'h-12 w-full rounded border border-line bg-transparent px-4 text-base outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { data: me } = useMe();
  const { data: config } = useAppConfig();
  const login = useLogin();
  const register = useRegister();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [validationError, setValidationError] = useState<string | null>(null);
  const from = (location.state as { from?: string } | null)?.from ?? paths.notes;
  const mutation = mode === 'login' ? login : register;

  if (me) return <Navigate to={from} replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setValidationError(null);
    const parsed = mode === 'login' ? loginSchema.safeParse(form) : registerSchema.safeParse(form);
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Formulaire invalide');
      return;
    }
    const onSuccess = () => navigate(from, { replace: true });
    if (mode === 'login') login.mutate(loginSchema.parse(form), { onSuccess });
    else register.mutate(registerSchema.parse(form), { onSuccess });
  };

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const error = validationError ?? (mutation.error ? errorMessage(mutation.error) : null);

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-[450px] rounded-lg border border-line px-6 py-10 sm:px-10">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/favicon.svg" alt="" className="size-12" />
          <h1 className="text-2xl">{mode === 'login' ? 'Connexion' : 'Créer un compte'}</h1>
          <p className="text-sm text-muted">
            {mode === 'login'
              ? 'Accédez à vos notes'
              : 'Vos notes, listes et rappels, sur votre serveur'}
          </p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {mode === 'register' && (
            <input
              className={inputClass}
              placeholder="Nom"
              autoComplete="name"
              value={form.name}
              onChange={set('name')}
              required
              autoFocus
            />
          )}
          <input
            className={inputClass}
            type="email"
            placeholder="Adresse e-mail"
            autoComplete="email"
            value={form.email}
            onChange={set('email')}
            required
            autoFocus={mode === 'login'}
          />
          <input
            className={inputClass}
            type="password"
            placeholder={mode === 'login' ? 'Mot de passe' : 'Mot de passe (8 caractères min.)'}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            value={form.password}
            onChange={set('password')}
            required
          />
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div className="mt-4 flex items-center justify-between gap-4">
            {mode === 'login' ? (
              config?.allowRegistration !== false ? (
                <Link
                  to={paths.register}
                  state={location.state}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Créer un compte
                </Link>
              ) : (
                <span />
              )
            ) : (
              <Link
                to={paths.login}
                state={location.state}
                className="text-sm font-medium text-primary hover:underline"
              >
                J'ai déjà un compte
              </Link>
            )}
            <Button type="submit" variant="primary" disabled={mutation.isPending}>
              {mode === 'login' ? 'Se connecter' : "S'inscrire"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
